import { ComponentType } from 'react';

import { IconProps } from '@/components/icons/AlertTriangleFilledIcon';
import { FileAttachment03Icon } from '@/components/icons/FileAttachment03Icon';
import { FileCheck02Icon } from '@/components/icons/FileCheck02Icon';
import { FileSearch02Icon } from '@/components/icons/FileSearch02Icon';
import { Lightbulb03Icon } from '@/components/icons/Lightbulb03Icon';
import { UserCircleIcon } from '@/components/icons/UserCircleIcon';
import { MemoPromptMemoPromptType } from '@/types/schema';

export const MEMOS_EMPTY_STATE_DESCRIPTION =
  'Generate fiduciary overviews, document comparisons, executive summaries, and cover letters for this household.';

export const TRUST_COMPARISON_DOCUMENT_LIMIT = 2;

interface MemoPromptConfig {
  createHeading: string;
  description: string;
  Icon: ComponentType<IconProps>;
  title: string;
}

/**
 * The memo prompt types a user can create from the memos page. CUSTOM memos
 * are agent-authored (e.g. the household chat agent's create-custom-memo
 * tool), never user-created, so they are excluded.
 */
export type CreatableMemoPromptType = Exclude<
  MemoPromptMemoPromptType,
  MemoPromptMemoPromptType.Custom
>;

export const MEMO_PROMPT_CONFIG: Record<
  CreatableMemoPromptType,
  MemoPromptConfig
> = {
  [MemoPromptMemoPromptType.EstatePlanSummary]: {
    createHeading: 'Create Choate estate plan summary',
    title: 'Choate estate plan summary',
    description:
      "Summarize the household's wills, trusts, fiduciaries, and key planning provisions.",
    Icon: FileCheck02Icon,
  },
  [MemoPromptMemoPromptType.RoleSummary]: {
    createHeading: 'Create fiduciary overview',
    title: 'Fiduciary overview',
    description:
      'Summarize responsibilities for key fiduciary and decision-maker roles.',
    Icon: UserCircleIcon,
  },
  [MemoPromptMemoPromptType.TrustComparison]: {
    createHeading: 'Create document comparison',
    title: 'Document comparison',
    description:
      'Compare two related documents, such as an original and its amendment, to highlight differences.',
    Icon: FileSearch02Icon,
  },
  [MemoPromptMemoPromptType.ExecutiveSummary]: {
    createHeading: 'Create executive summary',
    title: 'Executive summary',
    description:
      'Create an executive-level estate plan summary of planning strategies.',
    Icon: Lightbulb03Icon,
  },
  [MemoPromptMemoPromptType.CoverLetter]: {
    createHeading: 'Create cover letter',
    title: 'Cover letter',
    description:
      'Generate a polished letter that introduces the household estate plan.',
    Icon: FileAttachment03Icon,
  },
};

export const MEMO_PROMPT_CARD_ORDER: CreatableMemoPromptType[] = [
  MemoPromptMemoPromptType.EstatePlanSummary,
  MemoPromptMemoPromptType.RoleSummary,
  MemoPromptMemoPromptType.TrustComparison,
  MemoPromptMemoPromptType.ExecutiveSummary,
  MemoPromptMemoPromptType.CoverLetter,
];
